/**
 * EntropySkillEffect — Poison tree Plague path apotheosis.
 *
 * Detonates all remaining DoT on every poisoned enemy within 4 tiles as
 * instant burst damage. Each poisoned enemy's entire remaining DoT budget —
 * every tick it would have delivered over the coming seconds — is cashed in
 * at once. The burst splashes 50% of the detonated damage to non-poisoned
 * enemies within 1.5 tiles of each detonated target.
 *
 * Implementation: on activate, iterates all enemies within detonation range.
 * For each enemy carrying active `poison` instances, computes total remaining
 * damage (intensity × stacks × remainingMs / tickIntervalMs per instance),
 * applies the Overripe vulnerability multiplier if the debuff is present,
 * delivers the burst through the damage pathway, and removes all `poison`
 * from the target. Then for each detonated target, splashes to non-poisoned
 * neighbors within splash range.
 *
 * Cooldown tier: very_long (4000ms).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Entropy (entropy)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal enemy representation for targeting. */
export interface EntropyTarget {
  readonly entityId: string;
  readonly position: { x: number; y: number };
}

/** Poison instance data needed for burst damage computation. */
export interface PoisonInstance {
  readonly intensity: number;
  readonly remainingMs: number;
  readonly stacks: number;
  readonly tickIntervalMs: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface EntropyDeps {
  readonly targeting: {
    getPlayerPosition(): { x: number; y: number };
    getEnemiesInRange(
      center: { x: number; y: number },
      rangeTiles: number,
    ): ReadonlyArray<EntropyTarget>;
  };
  readonly statusEffects: {
    hasPoisonStatus(entityId: string): boolean;
    getPoisonInstances(entityId: string): ReadonlyArray<PoisonInstance>;
    hasDebuff(entityId: string, defId: string): boolean;
    removePoisonStatus(entityId: string): void;
  };
  readonly damage: {
    applyBurst(entityId: string, amount: number, sourceSkillId: string): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface EntropyConfig {
  /** Cooldown in ms (very_long tier = 4000). */
  readonly cooldownMs: number;
  /** Detonation AoE radius in tiles. */
  readonly detonationRangeTiles: number;
  /** Splash radius around each detonated target in tiles. */
  readonly splashRangeTiles: number;
  /** Fraction of burst damage applied as splash (0.5 = 50%). */
  readonly splashDamagePercent: number;
}

const DEFAULT_CONFIG: EntropyConfig = {
  cooldownMs: 4000,              // very_long tier
  detonationRangeTiles: 4,
  splashRangeTiles: 1.5,
  splashDamagePercent: 0.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class EntropySkillEffect implements SkillEffect {
  readonly skillId = 'entropy';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: EntropyDeps;
  private readonly config: EntropyConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;

  constructor(deps: EntropyDeps, config: Partial<EntropyConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const playerPos = this.deps.targeting.getPlayerPosition();
    const enemies = this.deps.targeting.getEnemiesInRange(
      playerPos,
      this.config.detonationRangeTiles,
    );

    // Phase 1: Detonate every poisoned enemy, collecting results for splash.
    const detonated = new Set<string>();
    const detonations: Array<{ target: EntropyTarget; burstDamage: number }> = [];

    for (const enemy of enemies) {
      if (!this.deps.statusEffects.hasPoisonStatus(enemy.entityId)) continue;

      const burst = this.computeBurstDamage(enemy.entityId);
      if (burst <= 0) continue;

      this.deps.damage.applyBurst(enemy.entityId, burst, 'entropy');
      this.deps.statusEffects.removePoisonStatus(enemy.entityId);
      detonated.add(enemy.entityId);
      detonations.push({ target: enemy, burstDamage: burst });
    }

    // Phase 2: Splash to non-poisoned neighbors of each detonated target.
    for (const { target, burstDamage } of detonations) {
      const splashDamage = burstDamage * this.config.splashDamagePercent;
      const nearby = this.deps.targeting.getEnemiesInRange(
        target.position,
        this.config.splashRangeTiles,
      );

      for (const neighbor of nearby) {
        // Skip the detonated target itself.
        if (neighbor.entityId === target.entityId) continue;
        // Skip other detonated targets (they were poisoned at activation time).
        if (detonated.has(neighbor.entityId)) continue;
        // Skip enemies that still carry poison (outside detonation range).
        if (this.deps.statusEffects.hasPoisonStatus(neighbor.entityId)) continue;

        this.deps.damage.applyBurst(neighbor.entityId, splashDamage, 'entropy');
      }
    }

    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;
    return true;
  }

  deactivate(): void {
    // Instant activation — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Sum remaining DoT damage across all `poison` instances on the target.
   *
   * Formula per instance: `intensity × stacks × (remainingMs / tickIntervalMs)`.
   * This equals per-tick damage × remaining ticks.
   *
   * Applies the Overripe vulnerability multiplier (1 + highDotDamageBonus)
   * if the target carries the `overripe_vulnerability` debuff.
   */
  private computeBurstDamage(entityId: string): number {
    const instances = this.deps.statusEffects.getPoisonInstances(entityId);
    let total = 0;

    for (const inst of instances) {
      if (inst.remainingMs <= 0) continue;
      total += inst.intensity * inst.stacks * (inst.remainingMs / inst.tickIntervalMs);
    }

    // Overripe: check for the vulnerability debuff applied by the Overripe
    // mechanic. The bonus value comes from the charnel_ground resolver scope.
    if (this.deps.statusEffects.hasDebuff(entityId, 'overripe_vulnerability')) {
      const bonus = this.deps.resolver.getAdditiveEffect(
        'charnel_ground',
        'highDotDamageBonus',
      );
      total *= (1 + bonus);
    }

    return total;
  }
}
